// ── Session time zone ─────────────────────────────────────────────────────
// Every timestamp shown to the user — on screen and in exports — is rendered
// in the time zone set on their MyGeotab user profile, NOT the browser's.
// Those differ constantly in practice: a dispatcher in Arizona pulling a
// report for a Texas fleet, a laptop that travelled, a VM left on UTC. A
// report that silently shifts every arrival by an hour against the MyGeotab
// screen it's being checked against is worse than useless, so the profile
// zone is the single source of truth here.

import { callGeotabAPI, getCurrentUserName } from '../api/geotab-client';
import type { User } from '../api/types';

/** Resolved IANA/Olson zone id from the Geotab profile, or null to mean "browser local". */
let _tz: string | null = null;
let _pending: Promise<string | null> | null = null;

/** True when `tz` is a zone Intl can actually format in (Geotab ids like "CST6CDT" are valid). */
function usable(tz: string | undefined | null): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * Fetch the signed-in user's profile time zone once per session. Any failure
 * degrades to browser-local rather than blocking the page — a report in the
 * wrong zone is a real problem, but a report that won't open at all is worse.
 */
export function ensureSessionTimeZone(): Promise<string | null> {
  if (_tz !== null) return Promise.resolve(_tz);
  if (_pending) return _pending;

  _pending = (async () => {
    try {
      const name = getCurrentUserName();
      if (!name) return null;
      const users = await callGeotabAPI<User[]>('Get', {
        typeName: 'User',
        search: { name },
        resultsLimit: 1,
      });
      const id = users?.[0]?.timeZoneId;
      if (usable(id)) { _tz = id; return _tz; }
    } catch (e) {
      console.warn('Session time zone lookup failed; using browser local time.', e);
    }
    return null;
  })();

  return _pending;
}

export function resetSessionTimeZone(): void {
  _tz = null;
  _pending = null;
}

/** The resolved zone id, or null when none was found (callers then get browser local). */
export function getSessionTimeZone(): string | null {
  return _tz;
}

/**
 * Short label for the active zone ("CDT", "MST"), for captioning a report so
 * a reader knows which clock the times are on. Falls back to the browser's
 * own abbreviation when no profile zone resolved.
 */
export function sessionTimeZoneLabel(atMs: number = Date.now()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: _tz ?? undefined,
      timeZoneName: 'short',
    }).formatToParts(new Date(atMs));
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Full descriptor for report metadata, e.g. "America/Chicago (CDT)". */
export function sessionTimeZoneDescription(atMs: number = Date.now()): string {
  const abbr = sessionTimeZoneLabel(atMs);
  if (!_tz) return abbr ? `Browser local (${abbr})` : 'Browser local';
  return abbr ? `${_tz} (${abbr})` : _tz;
}

/**
 * Hour-of-day 0–23 on the session clock. The card histograms bucket by this,
 * so a "busiest hour" reads in the fleet's own working day rather than the
 * browser's.
 */
export function hourInSessionTz(ms: number): number {
  try {
    const h = new Intl.DateTimeFormat('en-US', {
      timeZone: _tz ?? undefined, hour: '2-digit', hour12: false,
    }).formatToParts(new Date(ms)).find(p => p.type === 'hour')?.value;
    return h === undefined ? new Date(ms).getHours() : Number(h) % 24;
  } catch {
    return new Date(ms).getHours();
  }
}

/** Format a timestamp in the session zone. Same option shape as toLocaleString. */
export function formatInSessionTz(
  ms: number,
  opts: Intl.DateTimeFormatOptions,
): string {
  return new Date(ms).toLocaleString(undefined, { ...opts, timeZone: _tz ?? undefined });
}

/**
 * A Date whose UTC fields hold the session zone's WALL-CLOCK reading of `ms`.
 *
 * Excel has no concept of time zones: a cell is a bare wall-clock value, and
 * exceljs serialises a JS Date by its UTC fields. Writing the raw Date would
 * therefore display UTC in the spreadsheet. Shifting the value so its UTC
 * fields already read as the target zone makes the cell show the correct
 * local time while staying a real date — so it still sorts, filters and
 * charts as a date, which a pre-formatted string would not.
 */
export function excelDateInSessionTz(ms: number): Date {
  if (!_tz) return new Date(ms);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: _tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(ms));

    const get = (t: string): number => Number(parts.find(p => p.type === t)?.value ?? '0');
    // Intl renders midnight as hour 24 in some engines under hour12:false.
    const hour = get('hour') % 24;
    return new Date(Date.UTC(
      get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'),
    ));
  } catch {
    return new Date(ms);
  }
}
